import { baza } from "@/lib/baza";

/**
 * The queue of hours entries waiting to reach the server — ADR-0004 §2.3.
 *
 * Every entry is written here first, online or offline. There is deliberately
 * no "send it straight away if there is a signal" branch: one path through the
 * code is one path through the tests, and the path that only runs on a good
 * connection is the one nobody exercises.
 *
 * The store lives in IndexedDB rather than localStorage (§2.8), and outside
 * React entirely: iOS restarts the app when the manager switches away, and an
 * entry that lived in a component would be gone with it.
 *
 * The queue is never cleared by signing out (§2.7). Somebody else signing in
 * on the same phone does not make another person's unsent work disappear.
 */

/** Mirrors `ZapisIn` in `app/api/sati.py`. */
export interface ZapisZaSlanje {
  /** Explicit confirmation after a sick-leave conflict; never inferred. */
  confirm_sick_leave?: boolean;
  /** Generated on the device before the entry leaves it: the idempotency key. */
  client_uuid: string;
  radnik_id: string;
  gradiliste_id: string;
  /** ISO date, `YYYY-MM-DD`. */
  datum: string;
  tip_zapisa: "rad" | "nenazocnost";
  vrsta_nenazocnosti?: string | null;
  /** `HH:MM`, from the site's default shift unless corrected. */
  pocetak?: string | null;
  kraj?: string | null;
  zastoji_min: number;
  teren: boolean;
  /**
   * Who typed it, recorded **now** rather than derived from the token at send
   * time (§2.7). A token can expire and somebody else can sign in on the same
   * phone before this is sent; the author is not whoever happened to be signed
   * in then.
   */
  unio_korisnik_id: string | null;
  /** Device clock, ISO. Informative — the clock may simply be wrong. */
  unio_vrijeme: string;
}

/** Where an entry stands on this device. */
export type StanjeStavke =
  /** Waiting for a send, or for the next one after a failed attempt. */
  | "ceka"
  /** The server refused it. A person has to change something. */
  | "odbijen";

export interface StavkaReda {
  zapis: ZapisZaSlanje;
  stanje: StanjeStavke;
  /** How many times a send has been attempted. Diagnostics, not a limit. */
  pokusaji: number;
  /** Why the server refused, in the words it used. Shown to the manager. */
  razlog?: string;
  /** When this entered the queue, ISO. Drives the 3-day and 5-day warnings. */
  dodano: string;
}

/**
 * Queue length past which something is wrong — §2.8. A queue that grows
 * quietly is a fault discovered at payroll, which is the worst possible time.
 */
export const NAJVECI_RED = 200;
/** Days after which an unsent entry is the manager's problem (§2.8). */
export const DANA_UPOZORENJE = 3;
/**
 * Days after which it is personnel's problem too. NN 55/24 čl. 15 gives seven
 * days from the day being recorded, so five leaves room to act.
 */
export const DANA_ESKALACIJA = 5;

/**
 * Put an entry in the queue.
 *
 * `put`, not `add`: the same `client_uuid` written twice is the same entry,
 * and the queue must behave like the server does about that. Editing an entry
 * that has not gone yet replaces it rather than producing a second one.
 */
export async function stavi(zapis: ZapisZaSlanje): Promise<StavkaReda> {
  const stavka: StavkaReda = {
    zapis,
    stanje: "ceka",
    pokusaji: 0,
    dodano: new Date().toISOString(),
  };
  await (await baza()).put("red", stavka);
  return stavka;
}

/** Everything in the queue, oldest first. */
export async function sve(): Promise<StavkaReda[]> {
  const stavke = await (await baza()).getAll("red");
  return stavke.sort((a, b) => a.dodano.localeCompare(b.dodano));
}

/** The entries a send should carry: waiting ones only. */
export async function zaSlanje(): Promise<StavkaReda[]> {
  const stavke = await sve();
  // A refused entry is deliberately left out. Re-sending it every minute
  // produces the same refusal every minute and buries the one thing that
  // would fix it: somebody correcting the entry.
  return stavke.filter((stavka) => stavka.stanje === "ceka");
}

export async function ukloni(clientUuid: string): Promise<void> {
  await (await baza()).delete("red", clientUuid);
}

/** Record what the server said about an entry it would not take. */
export async function oznaciOdbijen(
  clientUuid: string,
  razlog: string | null,
): Promise<void> {
  const db = await baza();
  const stavka = await db.get("red", clientUuid);
  if (!stavka) {
    return;
  }
  await db.put("red", {
    ...stavka,
    stanje: "odbijen",
    razlog: razlog ?? undefined,
    pokusaji: stavka.pokusaji + 1,
  });
}

/**
 * Put a refused entry back in the queue, corrected.
 *
 * The **same `client_uuid`**, deliberately: it is the idempotency key, and a
 * correction is the same entry changed rather than a second one. A new key
 * here would mean an entry the server had in fact stored could arrive twice.
 *
 * `dodano` is kept as it was. It drives the three- and five-day warnings, and
 * an entry that has been stuck since Monday is still stuck since Monday after
 * somebody fixes the times — resetting it would hide exactly the case the
 * warning exists for. `pokusaji` is kept for the same reason: it is
 * diagnostics, and the attempts did happen.
 */
export async function ispravi(zapis: ZapisZaSlanje): Promise<void> {
  const db = await baza();
  const stavka = await db.get("red", zapis.client_uuid);
  if (!stavka) {
    return;
  }
  await db.put("red", {
    ...stavka,
    zapis,
    stanje: "ceka",
    // The server's words are about what was sent, and what was sent has just
    // changed. Keeping them would leave a corrected entry showing the reason
    // its previous version was refused.
    razlog: undefined,
  });
}

/** Count an attempt that did not get an answer — the entry stays waiting. */
export async function zabiljeziPokusaj(clientUuids: string[]): Promise<void> {
  const db = await baza();
  const transakcija = db.transaction("red", "readwrite");
  await Promise.all(
    clientUuids.map(async (uuid) => {
      const stavka = await transakcija.store.get(uuid);
      if (stavka) {
        await transakcija.store.put({
          ...stavka,
          pokusaji: stavka.pokusaji + 1,
        });
      }
    }),
  );
  await transakcija.done;
}

export interface StanjeReda {
  /** Entries waiting to be sent. */
  ceka: number;
  /** Entries the server refused; each needs a person. */
  odbijeno: number;
  /** Age in days of the oldest waiting entry, or null when there is none. */
  najstarijeDana: number | null;
  /** The oldest waiting entry has passed the 3-day mark (§2.8). */
  zastarjelo: boolean;
  /** The queue is longer than it has any business being (§2.8). */
  predugacak: boolean;
}

/** What the interface shows about the queue, computed in one place. */
export async function stanje(sada: Date = new Date()): Promise<StanjeReda> {
  const stavke = await sve();
  const ceka = stavke.filter((stavka) => stavka.stanje === "ceka");
  const odbijeno = stavke.length - ceka.length;

  // `sve` sorts oldest first, so the head of the waiting list is the entry the
  // deadline applies to.
  const najstarije = ceka[0];
  const najstarijeDana = najstarije
    ? Math.floor(
        (sada.getTime() - new Date(najstarije.dodano).getTime()) / 86_400_000,
      )
    : null;

  return {
    ceka: ceka.length,
    odbijeno,
    najstarijeDana,
    zastarjelo: najstarijeDana !== null && najstarijeDana >= DANA_UPOZORENJE,
    predugacak: stavke.length > NAJVECI_RED,
  };
}
